/* =========================================================================
   Grivet Shoe Quiz — Scoring + Profile Name
   Pure functions. Exposed on window.GrivetQuizScoring for use by shoe-quiz.js.
   ========================================================================= */
(function () {
  'use strict';

  function mileageMidpoint(q6) {
    return {
      vol_1_10: 5, vol_11_25: 18, vol_26_40: 33, vol_41_plus: 50, vol_new: 0,
      hike_rare: 0, hike_monthly: 0, hike_weekly: 0, hike_thru: 0,
      life_light: 0, life_med: 0, life_heavy: 0
    }[q6] || 0;
  }

  function weightClassToNumber(wc) {
    return { light: 1, moderate: 3, stable: 5 }[wc] || 3;
  }

  function dropInRange(mm, pref) {
    if (!pref || pref === 'unknown') return false;
    if (pref === 'low') return mm <= 4;
    if (pref === 'mid') return mm >= 5 && mm <= 8;
    if (pref === 'traditional') return mm >= 8;
    return false;
  }

  function normalizeSize(s) {
    return String(s || '').trim().replace(/\s+.*$/, '').replace(/\.0+$/, '').replace(/^0+(?=\d)/, '');
  }
  function sizeAvailable(shoe, size, width, opts) {
    if (!shoe.variants || !shoe.variants.length) return true; // fallback: assume available
    const sizeStr = normalizeSize(size);
    const relaxWidth = !!(opts && opts.relaxWidth);
    const widthWanted = relaxWidth ? null : ((width === 'unsure' || !width) ? null : width);
    return shoe.variants.some(v => {
      if (!v.available) return false;
      if (sizeStr && v.size && normalizeSize(v.size) !== sizeStr) return false;
      if (widthWanted && v.width && v.width !== widthWanted) return false;
      return true;
    });
  }

  function normalize(text) {
    return String(text || '').toLowerCase().trim();
  }

  function extractLovedBrands(q4) {
    const brands = [];
    const text = normalize((q4 && q4.loved) || '');
    if (!text) return brands;
    const knownBrands = ['brooks','hoka','saucony','on','on running','asics','altra','topo','mizuno','new balance','nike','adidas','salomon','merrell'];
    knownBrands.forEach(b => {
      if (text.includes(b)) brands.push(b === 'on running' ? 'On Running' : b.replace(/\b\w/g, c => c.toUpperCase()));
    });
    return Array.from(new Set(brands));
  }

  function extractDislikedBrands(q4) {
    return extractLovedBrands({ loved: (q4 && q4.disliked) || '' });
  }

  function scoreShoe(shoe, answers, opts) {
    opts = opts || {};
    if (shoe.quiz_eligible === false) return { score: -Infinity, reason: 'not eligible' };
    const activities = shoe.activity_type || [];
    const gifActivity = answers.q1 === 'gift' ? (answers.q2 || answers.q1) : answers.q1;
    if (!activities.includes(gifActivity) && !activities.includes(answers.q1)) {
      return { score: -Infinity, reason: 'wrong activity' };
    }

    const wantedGender = answers.q8.gender;
    const relaxGender = !!opts.relaxGender;
    if (!relaxGender && wantedGender && wantedGender !== 'open' && shoe.gender && shoe.gender !== wantedGender && shoe.gender !== 'unisex') {
      return { score: -Infinity, reason: 'gender mismatch' };
    }
    const inStock = sizeAvailable(shoe, answers.q8.size, answers.q8.width, { relaxWidth: opts.relaxWidth });
    if (!inStock && !opts.relaxSize) {
      return { score: -Infinity, reason: 'size/width unavailable' };
    }

    let score = 100;
    const reasons = [];

    // Surface (Q2)
    const surface = answers.q2;
    if (surface && (shoe.surface || []).includes(surface)) { score += 20; reasons.push('matches your surface'); }

    // Arch (Q3)
    if (answers.q3 && answers.q3 !== 'arch_unknown') {
      if ((shoe.arch_fit || []).includes(answers.q3)) { score += 15; reasons.push('fits your arch'); }
      else score -= 10;
    }

    // Fit history (Q4)
    const loved = extractLovedBrands(answers.q4);
    const disliked = extractDislikedBrands(answers.q4);
    const shoeBrand = normalize(shoe.brand);
    loved.forEach(b => { if (normalize(b) === shoeBrand) { score += 15; reasons.push(`you love ${b}`); } });
    (shoe.similar_last_brands || []).forEach(b => {
      if (loved.some(lb => normalize(lb) === normalize(b))) { score += 5; }
    });
    disliked.forEach(b => { if (normalize(b) === shoeBrand) score -= 20; });

    // Pain / goals (Q5)
    const q5 = answers.q5 || [];
    q5.forEach(flag => {
      if ((shoe.best_for_pain || []).includes(flag)) { score += 10; reasons.push('addresses ' + flag.replace('pain_','').replace('goal_','')); }
      if (flag === 'goal_cushion') score += ((shoe.cushion_level || 3) - 3) * 4;
      if (flag === 'goal_light') score -= ((shoe.cushion_level || 3) - 2) * 3;
      if (flag === 'goal_stability' && shoe.stability && shoe.stability !== 'neutral') { score += 8; reasons.push('extra stability'); }
      if (flag === 'goal_zero_drop' && (shoe.drop_mm || 12) <= 4) { score += 12; reasons.push('low drop'); }
      if (flag === 'goal_wide' && !(shoe.width_options || []).includes('wide')) score -= 30;
    });

    // Volume (Q6)
    const userMiles = mileageMidpoint(answers.q6);
    if (userMiles > 0 && typeof shoe.mileage_range_min === 'number' && typeof shoe.mileage_range_max === 'number') {
      if (userMiles >= shoe.mileage_range_min && userMiles <= shoe.mileage_range_max) {
        score += 10;
        reasons.push(userMiles + 'mi/week sweet spot');
      }
    }

    // Sliders (Q7)
    const cushDiff = Math.abs(answers.q7.cushion - (shoe.cushion_level || 3));
    score -= cushDiff * 4;
    const feelDiff = Math.abs(answers.q7.feel - weightClassToNumber(shoe.weight_class));
    score -= feelDiff * 3;

    // Drop preference (advanced)
    if (answers.q7.drop) {
      if (dropInRange(shoe.drop_mm || 12, answers.q7.drop)) score += 8;
    }

    return { score, reasons };
  }

  function selectTop3(scored) {
    const sorted = scored
      .filter(s => isFinite(s.score))
      .sort((a, b) => b.score - a.score);
    if (!sorted.length) return [];
    const topMatch = sorted[0];
    const runnerUp = sorted[1] || null;
    let alternative = null;
    if (runnerUp) {
      for (const s of sorted.slice(2)) {
        const brandDiff = normalize(s.shoe.brand) !== normalize(topMatch.shoe.brand);
        const cushDiff = Math.abs((s.shoe.cushion_level || 3) - (topMatch.shoe.cushion_level || 3)) >= 2;
        if (s.shoe.id !== topMatch.shoe.id && s.shoe.id !== runnerUp.shoe.id && (brandDiff || cushDiff)) {
          alternative = s;
          break;
        }
      }
      if (!alternative) alternative = sorted[2] || null;
    }
    return [topMatch, runnerUp, alternative].filter(Boolean);
  }

  function generateProfileName(answers) {
    const vol = answers.q6;
    const volume = {
      vol_1_10: 'Casual', vol_11_25: 'Dedicated', vol_26_40: 'High-Mileage',
      vol_41_plus: 'Elite', vol_new: 'Aspiring',
      hike_rare: 'Weekend', hike_monthly: 'Regular', hike_weekly: 'Avid', hike_thru: 'Thru-Hiker',
      life_light: 'Everyday', life_med: 'On-the-Go', life_heavy: 'All-Day'
    }[vol] || '';
    const stability = {
      arch_low: 'Stability', arch_neutral: 'Neutral', arch_high: 'Cushioned', arch_unknown: ''
    }[answers.q3] || '';
    const surface = {
      run_road: 'Roadie', run_trail: 'Trail Runner', hike: 'Hiker',
      lifestyle: 'Walker', gift: ''
    }[answers.q1] || 'Athlete';
    return [volume, stability, surface].filter(Boolean).join(' ');
  }

  function buildReasoning(answers) {
    const parts = [];
    const q1 = { run_road: 'running roads', run_trail: 'trail running', hike: 'hiking', lifestyle: 'walking and everyday wear', gift: 'shopping for someone' }[answers.q1];
    if (q1) parts.push('Because you\'re ' + q1);
    if (answers.q6 && mileageMidpoint(answers.q6) > 0) parts.push('at ~' + mileageMidpoint(answers.q6) + ' mi/week');
    if (answers.q3 && answers.q3 !== 'arch_unknown') parts.push('with a ' + answers.q3.replace('arch_','') + ' arch');
    const cushion = answers.q7.cushion >= 4 ? 'plush cushion' : (answers.q7.cushion <= 2 ? 'a firm, responsive feel' : 'balanced cushion');
    parts.push('wanting ' + cushion);
    return parts.join(', ') + ', we ranked shoes that fit that profile.';
  }

  // Tiered-fallback recommendations: try strict first, then widen criteria if we
  // don't have enough in-stock matches. Each fallback tier annotates its
  // results with `fallback_note` so the UI can show a caveat to the customer.
  function selectRecommendations(shoes, answers) {
    function runTier(opts, note) {
      const scored = shoes.map(shoe => {
        const r = scoreShoe(shoe, answers, opts);
        return { shoe, score: r.score, reasons: r.reasons || [], fallback_note: note };
      });
      return selectTop3(scored);
    }

    // Tier 0: exact match (wanted width + wanted gender)
    let picks = runTier({}, null);
    if (picks.length >= 3) return { picks, tier: 'strict' };

    // Tier 1: allow any width (e.g., customer wants "wide" but only "standard" is in stock in their size)
    const wanted = answers.q8 && answers.q8.width;
    if (wanted && wanted !== 'unsure' && wanted !== 'standard') {
      const relaxed = runTier({ relaxWidth: true }, 'Your preferred width isn\'t in stock in your size — showing closest fit.');
      if (relaxed.length > picks.length) picks = relaxed;
      if (picks.length >= 3) return { picks, tier: 'relax_width' };
    }

    // Tier 2: allow cross-gender (pulls in unisex/other-gender options when the pool is thin)
    const relaxed2 = runTier({ relaxWidth: true, relaxGender: true }, 'Showing closest available matches — a few may be outside your preferred gender fit.');
    if (relaxed2.length > picks.length) picks = relaxed2;
    if (picks.length >= 3) return { picks, tier: 'relax_gender' };

    // Tier 3 (last resort): your exact size isn't in stock anywhere in this
    // activity pool — surface the top-scoring shoes regardless of stock so the
    // customer sees something buyable in other sizes / can request a restock.
    const relaxed3 = runTier({ relaxWidth: true, relaxGender: true, relaxSize: true },
      'Nothing in your exact size is in stock right now — here are the best profile fits. Check the product page for other sizes or join the restock list.');
    if (relaxed3.length > picks.length) picks = relaxed3;
    return { picks, tier: picks.length >= 3 ? 'relax_size' : 'insufficient' };
  }

  window.GrivetQuizScoring = { scoreShoe, selectTop3, selectRecommendations, generateProfileName, buildReasoning, mileageMidpoint };
})();
